import {ReactElement} from 'react'
import {graphql} from 'react-apollo'
import {get, flowRight as compose} from 'lodash'
import {Link, withRouter} from 'react-router-dom'
import {Trans, withNamespaces} from 'react-i18next'
import moment from 'moment'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import makeStyles from '@material-ui/core/styles/makeStyles'

import messages from '../../../assets/messages'
import {getFormattedAmount} from '../../../common/utils/general'
import {
  getTauroFactorisationFromAccount,
  formatTauroDrawdownPercent,
} from '../../../common/utils/accounts'
import {Account} from '../../../graphql/types'
import {FOREX_ACCOUNT_QUERY} from '../../../graphql/queries'
import {
  getClosedFactorisedAccounts,
  getFundedAccountEquity,
  getFundedAccountPosition,
  isFundedAccountClosed,
} from './fundedAccount'

const useStyles = makeStyles((theme) => ({
  card: {padding: theme.spacing(3), marginBottom: theme.spacing(3)},
  figures: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  figure: {minWidth: 140},
  label: {color: theme.palette.text.secondary},
  bar: {
    position: 'relative' as const,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#FBE3E1',
    overflow: 'visible',
  },
  fill: {height: '100%', borderRadius: 6, backgroundColor: '#2E9E5B'},
  marker: {
    position: 'absolute' as const,
    top: -4,
    width: 2,
    height: 20,
    backgroundColor: theme.palette.text.primary,
  },
  barLabels: {display: 'flex', justifyContent: 'space-between', marginTop: theme.spacing(1)},
  historyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 0),
    borderTop: '1px solid #E5E7EB',
  },
}))

type ForexAccountFundedProps = {
  account: Account
  accounts: Account[]
  /** Live equity from the polled query. Null until the first answer, then the cached value shows. */
  liveEquity?: number | null
  t: (key: string, defaults?: string) => string
}

/**
 * The "Funded" tab of a factorised account (PT-7656): the starting allocation, the equity
 * floor where the liquidation starts, and where the account stands against that floor. Below
 * the figures it lists the client's earlier closed factorised accounts. Every number comes
 * from the snapshot the account was opened with and from the live margin data, which the
 * tab polls every 5 seconds like the Overview tab does: an equity that stands still while a
 * position loses money would hide how close the account is to the floor.
 */
function ForexAccountFunded(props: ForexAccountFundedProps): ReactElement | null {
  const {account, accounts, liveEquity, t} = props
  const classes = useStyles()
  const factorisation = getTauroFactorisationFromAccount(account)
  if (factorisation == null) return null

  const currency = factorisation.currency ?? 'USD'
  const isClosed = isFundedAccountClosed(account)
  const position = getFundedAccountPosition(
    factorisation,
    isClosed ? null : (liveEquity ?? getFundedAccountEquity(account)),
  )
  const closed = getClosedFactorisedAccounts(accounts, (account as {id: number}).id)
  const amount = (value: number) => getFormattedAmount({currency, amount: value})

  return (
    <>
      <Paper className={classes.card}>
        {position == null ? (
          <Typography>
            <Trans {...messages.fundedNotFunded} />
          </Typography>
        ) : (
          <>
            <div className={classes.figures}>
              <div className={classes.figure}>
                <Typography variant="caption" className={classes.label}>
                  <Trans {...messages.fundedStartingAllocation} />
                </Typography>
                <Typography variant="h6">{amount(position.startingBalance)}</Typography>
              </div>
              <div className={classes.figure}>
                <Typography variant="caption" className={classes.label}>
                  <Trans
                    {...messages.fundedMaxDrawdown}
                    values={{
                      percent: formatTauroDrawdownPercent(
                        factorisation.drawdownTriggerPercent ?? 0,
                      ),
                    }}
                  />
                </Typography>
                <Typography variant="h6">{amount(position.floor)}</Typography>
              </div>
              {!isClosed && (
                <>
                  <div className={classes.figure}>
                    <Typography variant="caption" className={classes.label}>
                      <Trans {...messages.fundedCurrentEquity} />
                    </Typography>
                    <Typography variant="h6">
                      {position.current != null ? (
                        amount(position.current)
                      ) : (
                        <Trans {...messages.fundedEquityUnavailable} />
                      )}
                    </Typography>
                  </div>
                  <div className={classes.figure}>
                    <Typography variant="caption" className={classes.label}>
                      <Trans {...messages.fundedDistanceToFloor} />
                    </Typography>
                    <Typography variant="h6">
                      {position.distanceToFloor != null ? (
                        amount(position.distanceToFloor)
                      ) : (
                        <Trans {...messages.fundedEquityUnavailable} />
                      )}
                    </Typography>
                  </div>
                </>
              )}
            </div>
            {isClosed && (
              <Typography className={classes.label}>
                <Trans {...messages.fundedAccountClosed} />
              </Typography>
            )}
            {position.currentPercent != null && position.startPercent != null && (
              <>
                <div
                  className={classes.bar}
                  role="img"
                  aria-label={t(messages.fundedBarLabel.i18nKey, messages.fundedBarLabel.defaults)}
                >
                  <div className={classes.fill} style={{width: `${position.currentPercent}%`}} />
                  <div className={classes.marker} style={{left: `${position.startPercent}%`}} />
                </div>
                <div className={classes.barLabels}>
                  <Typography variant="caption" className={classes.label}>
                    <Trans
                      {...messages.fundedFloorLabel}
                      values={{amount: amount(position.floor)}}
                    />
                  </Typography>
                  <Typography variant="caption" className={classes.label}>
                    <Trans
                      {...messages.fundedStartMarkerLabel}
                      values={{amount: amount(position.startingBalance)}}
                    />
                  </Typography>
                </div>
              </>
            )}
          </>
        )}
      </Paper>
      <Paper className={classes.card}>
        <Typography variant="h6" gutterBottom>
          <Trans {...messages.fundedClosedAccountsTitle} />
        </Typography>
        {closed.length === 0 ? (
          <Typography className={classes.label}>
            <Trans {...messages.fundedClosedAccountsEmpty} />
          </Typography>
        ) : (
          closed.map((closedAccount) => {
            const closedFactorisation = getTauroFactorisationFromAccount(closedAccount)
            const closedCurrency = closedFactorisation?.currency ?? currency
            const liquidation = closedFactorisation?.liquidation ?? null
            const closedAt = liquidation?.closedAt ?? liquidation?.triggeredAt ?? null
            return (
              <div className={classes.historyRow} key={(closedAccount as {id: number}).id}>
                <Link to={`/accounts/${(closedAccount as {id: number}).id}`}>
                  <Trans
                    {...messages.fundedClosedAccountName}
                    values={{
                      multiplier: closedFactorisation?.multiplier ?? '',
                      accountId:
                        (closedAccount as unknown as {remoteId?: string | null}).remoteId ?? '',
                    }}
                  />
                </Link>
                <Typography variant="body2" className={classes.label}>
                  {closedFactorisation?.startingBalance != null &&
                    getFormattedAmount({
                      currency: closedCurrency,
                      amount: closedFactorisation.startingBalance,
                    })}
                  {closedAt ? ` · ${moment.utc(closedAt).format('D MMM YYYY')}` : ''}
                  {liquidation != null && (
                    <>
                      {' · '}
                      <Trans {...messages.fundedClosedLiquidated} />
                    </>
                  )}
                </Typography>
              </div>
            )
          })
        )}
      </Paper>
    </>
  )
}

export default compose(
  withNamespaces(),
  withRouter,
  graphql(FOREX_ACCOUNT_QUERY, {
    options: (props: any) => ({
      variables: {id: Number(props.match.params.accountId)},
      pollInterval: 5000,
      fetchPolicy: 'network-only',
      skip: Boolean(get(props, 'account.isArchived')),
    }),
    props: ({data}: any) => ({
      liveEquity: get(data, 'viewer.accounts.0.marginInfo.equity', null),
    }),
  }),
)(ForexAccountFunded)
